import { Bell } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { type ComponentProps, useCallback } from 'react';

import type { PaginationFilter } from '@/types/PaginationFilter';

import SearchFilter from '@/components/Pagination/hammer-pagination/SearchFilter';
import SortPageSizePair from '@/components/Pagination/hammer-pagination/SortPageSizePair';
import { similarItemAlertsSortOrders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { getSimilarItemAlertsUnfilteredCount } from '@/redux/modules/similarItemAlerts/similarItemAlerts.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getNotificationsUrl } from '@/utils/urls';

type Props = {
    filters: PaginationFilter;
};

export const MySimilarItemAlertsFilter = ({ filters }: Props) => {
    const formatMessage = useAppFormatMessage();
    // The bidder's total alert count, not the filtered one — the placeholder must not count down
    // as they type.
    const unfilteredCount = useAppSelector(getSimilarItemAlertsUnfilteredCount);

    // Both controls write straight to the URL (`shouldModifyQueryParams`), so there's no filter
    // state here to react to -- they each require an onChange regardless.
    const noopFilterChange = useCallback(() => {}, []);

    const sortOptions: ComponentProps<typeof SortPageSizePair>['sortOptions'] = [
        {
            label: String(formatMessage(['similarItemAlerts', 'sort', 'addedNewest'])),
            value: similarItemAlertsSortOrders.savedNewest,
        },
        {
            label: String(formatMessage(['similarItemAlerts', 'sort', 'addedOldest'])),
            value: similarItemAlertsSortOrders.savedOldest,
        },
        {
            label: String(formatMessage(['similarItemAlerts', 'sort', 'itemNameAZ'])),
            value: similarItemAlertsSortOrders.titleAZ,
        },
    ];

    return (
        <div className="flex flex-row items-center justify-between pt-lg xsMax:flex-col xsMax:items-start">
            <div className="flex gap-sm xsMax:w-full xsMax:flex-col-reverse">
                <SearchFilter
                    className="w-[240px] xsMax:w-full [&_label]:hidden"
                    onChange={noopFilterChange}
                    paginationFilter={filters}
                    searchPlaceholder={String(
                        formatMessage(['similarItemAlerts', 'searchPlaceholder'], {
                            values: { count: unfilteredCount },
                        })
                    )}
                    shouldModifyQueryParams
                    shouldResetPageOnChange
                />
                <SortPageSizePair
                    className="w-full"
                    hideCount
                    isInsideLabel
                    onChange={noopFilterChange}
                    paginationFilter={filters}
                    shouldModifyQueryParams
                    sortLabel={formatMessage(['similarItemAlerts', 'sortLabel'])}
                    sortOptions={sortOptions}
                />
            </div>
            {/* No `id` here, unlike the Followed Searches filter bar this mirrors: Tabs keeps every
                panel mounted, so both bars are in the DOM at once and a shared id would duplicate. */}
            <SubtleMonochromeLink
                className="xsMax:hidden"
                data-testid="manageSimilarItemAlertNotifications"
                href={getNotificationsUrl('similarItemAlerts')}
            >
                <InlineButton icon={Bell}>{formatMessage(['manageNotifications'])}</InlineButton>
            </SubtleMonochromeLink>
        </div>
    );
};
